// types
import type { UserAccessGrant, UserAccessScopeType } from '@/types/domain/user-access'

/**
 * A dealership as returned by the permissions endpoint's resolved access list.
 */
export interface AccessDealership {
  id: number
  name: string
  type?: string | null
  orgId: number | null
  orgName?: string | null
  manufacturerId: number | null
  manufacturerName?: string | null
  region: string | null
  [key: string]: unknown
}

/** Region entry derived from the dealership list */
export interface AccessRegion {
  id: string
  name: string
}

/** Manufacturer entry derived from the dealership list */
export interface AccessManufacturer {
  id: number
  name: string
  regions: string[]
}

/** Org (legacy "group") entry derived from the dealership list */
export interface AccessOrg {
  id: number
  name: string
  regions: string[]
  manufacturers: number[]
}

/** The full set of client-side groupings derived from a flat dealership list */
export interface AccessGroupings {
  regions: AccessRegion[]
  manufacturers: AccessManufacturer[]
  groups: AccessOrg[]
}

/** Display names for the region codes used across the platform */
const REGION_NAMES: Record<string, string> = {
  gb: 'United Kingdom',
  us: 'United States',
  es: 'Spain',
  fr: 'France',
  de: 'Germany',
  mx: 'Mexico'
}

/**
 * Resolve the display name for a region code, falling back to the code itself.
 *
 * @param code - Two letter country code (case insensitive)
 * @returns Human readable region name
 */
export function getRegionName(code: string): string {
  const region = (code ?? '').toLowerCase()

  return REGION_NAMES[region] ?? region.toUpperCase()
}

/**
 * Check that an identifier is a usable numeric ID.
 *
 * @param value - The value to test
 * @returns True when the value is a finite number
 */
function isNumericId(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

/**
 * Derive region / manufacturer / org groupings from a flat resolved dealership
 * list. Access grouping is a pure UI concern: the API only returns the
 * dealerships a user can access, and the FE builds the filter hierarchies.
 *
 * @param dealerships - Resolved dealership list from the permissions endpoint
 * @returns Groupings sorted alphabetically by name
 */
export function buildAccessGroupings(dealerships: AccessDealership[]): AccessGroupings {
  const regions = new Map<string, AccessRegion>()
  const manufacturers = new Map<number, AccessManufacturer>()
  const orgs = new Map<number, AccessOrg>()

  for (const dealership of dealerships) {
    const region = (dealership.region ?? '').toLowerCase()

    // Region: unique per country code
    if (region && !regions.has(region)) {
      regions.set(region, { id: region, name: getRegionName(region) })
    }

    // Manufacturer: only franchised dealerships carry one
    if (isNumericId(dealership.manufacturerId)) {
      let manufacturer = manufacturers.get(dealership.manufacturerId)
      // First occurrence creates the entry
      if (!manufacturer) {
        manufacturer = {
          id: dealership.manufacturerId,
          name: dealership.manufacturerName ?? String(dealership.manufacturerId),
          regions: []
        }
        manufacturers.set(dealership.manufacturerId, manufacturer)
      }
      // Track every region the manufacturer operates in
      if (region && !manufacturer.regions.includes(region)) {
        manufacturer.regions.push(region)
      }
    }

    // Org: every dealership belongs to one, but guard against unusable IDs
    // rather than grouping unrelated sites under a placeholder org
    if (!isNumericId(dealership.orgId)) {
      continue
    }

    let org = orgs.get(dealership.orgId)
    // First occurrence creates the entry
    if (!org) {
      org = {
        id: dealership.orgId,
        name: dealership.orgName ?? `Org ${dealership.orgId}`,
        regions: [],
        manufacturers: []
      }
      orgs.set(dealership.orgId, org)
    }
    // Track the org's regions and manufacturers
    if (region && !org.regions.includes(region)) {
      org.regions.push(region)
    }
    if (isNumericId(dealership.manufacturerId) && !org.manufacturers.includes(dealership.manufacturerId)) {
      org.manufacturers.push(dealership.manufacturerId)
    }
  }

  const byName = <T extends { name: string }>(a: T, b: T): number => a.name.localeCompare(b.name)

  return {
    regions: [...regions.values()].sort(byName),
    manufacturers: [...manufacturers.values()].sort(byName),
    groups: [...orgs.values()].sort(byName)
  }
}

/** The location filter selection, kept in sync with the picked dealerships */
export interface AccessSelectionGroupings {
  regions: string[]
  manufacturers: number[]
  groups: number[]
  dealerships: number[]
}

/**
 * Derive the full location selection from a set of picked dealerships. The
 * region / manufacturer / org arrays are a read-only reflection of the sites
 * chosen, so they can never drift out of step with them.
 *
 * @param dealershipIds - The picked dealership IDs
 * @param dealerships - The dealership pool to derive from
 * @returns The selection, with unknown dealership IDs dropped
 */
export function deriveAccessSelection(
  dealershipIds: number[],
  dealerships: AccessDealership[]
): AccessSelectionGroupings {
  const picked = new Set(dealershipIds)
  const regions = new Set<string>()
  const manufacturers = new Set<number>()
  const groups = new Set<number>()
  const selected: number[] = []

  for (const dealership of dealerships) {
    // Only picked sites contribute, and only sites that still exist
    if (!picked.has(dealership.id)) {
      continue
    }

    selected.push(dealership.id)

    const region = (dealership.region ?? '').toLowerCase()
    if (region) {
      regions.add(region)
    }
    if (isNumericId(dealership.manufacturerId)) {
      manufacturers.add(dealership.manufacturerId)
    }
    if (isNumericId(dealership.orgId)) {
      groups.add(dealership.orgId)
    }
  }

  return {
    regions: [...regions],
    manufacturers: [...manufacturers],
    groups: [...groups],
    dealerships: selected.sort((a, b) => a - b)
  }
}

/**
 * Resolve the dealership IDs a set of grants gives access to, mirroring the
 * API-side union resolution (used for client-side previews before saving).
 *
 * - `all` matches every dealership
 * - `org` matches dealerships in that org
 * - `manufacturer` matches franchised dealerships of that manufacturer,
 *   optionally narrowed to a region (brandless sites can never match)
 * - `dealership` matches that single dealership
 *
 * @param grants - The grants to resolve
 * @param dealerships - The dealership pool to resolve against
 * @returns Sorted, de-duplicated dealership IDs
 */
export function resolveDealershipIdsFromGrants(
  grants: Pick<UserAccessGrant, 'scopeType' | 'scopeId' | 'region'>[],
  dealerships: AccessDealership[]
): number[] {
  const ids = new Set<number>()

  for (const grant of grants) {
    for (const dealership of dealerships) {
      if (grantMatchesDealership(grant, dealership)) {
        ids.add(dealership.id)
      }
    }
  }

  return [...ids].sort((a, b) => a - b)
}

/**
 * Check whether a single grant gives access to a single dealership.
 *
 * @param grant - The grant to test
 * @param dealership - The dealership to test against
 * @returns True when the grant covers the dealership
 */
function grantMatchesDealership(
  grant: { scopeType: UserAccessScopeType; scopeId?: number | null; region?: string | null },
  dealership: AccessDealership
): boolean {
  switch (grant.scopeType) {
    case 'all':
      return true
    case 'org':
      return dealership.orgId === grant.scopeId
    case 'manufacturer':
      // Brandless (multi-brand / used supermarket) sites never match manufacturer grants
      if (dealership.manufacturerId === null || dealership.manufacturerId !== grant.scopeId) {
        return false
      }
      // A region qualifier narrows the grant to dealerships in that region
      return !grant.region || (dealership.region ?? '').toLowerCase() === grant.region.toLowerCase()
    case 'dealership':
      return dealership.id === grant.scopeId
    default:
      return false
  }
}
